import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { AuthCardLayout } from '@/components/AuthCardLayout'
import { InputOTP } from '@/components/ui/input-otp'
import { FormInput } from '@/components/form'
import { useAuth } from '@/contexts/authContext'
import { authService } from '@/services/auth'
import { ROUTES } from '@/constants/routes'
import { queryKeys } from '@/lib/query-keys'
import { getApiErrorMessage } from '@/utils/errors'

const TOTP_LENGTH = 6

interface LocationState {
  temporaryToken?: string
  from?: string
}

export function Verify2FAPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { refreshAuth } = useAuth()
  const state = (location.state ?? {}) as LocationState
  const { temporaryToken, from = ROUTES.DASHBOARD } = state

  const [useRecoveryCode, setUseRecoveryCode] = useState(false)
  const [totpCode, setTotpCode] = useState('')
  const [recoveryCode, setRecoveryCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const submittedTotpRef = useRef('')

  useEffect(() => {
    if (!temporaryToken) {
      navigate(ROUTES.LOGIN, { replace: true })
    }
  }, [temporaryToken, navigate])

  const verifyWithTotp = async (code: string) => {
    if (!temporaryToken || code.length !== TOTP_LENGTH) return
    setError(null)
    setIsSubmitting(true)
    try {
      await authService.verify2FA(temporaryToken, { totp_code: code })
      queryClient.removeQueries({ queryKey: queryKeys.users.me })
      refreshAuth()
      navigate(from, { replace: true })
    } catch (err) {
      setError(getApiErrorMessage(err, t('login2FA.invalidCode')))
      setTotpCode('')
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (
      !useRecoveryCode &&
      totpCode.length === TOTP_LENGTH &&
      temporaryToken &&
      !isSubmitting &&
      submittedTotpRef.current !== totpCode
    ) {
      submittedTotpRef.current = totpCode
      void verifyWithTotp(totpCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totpCode, useRecoveryCode, temporaryToken, isSubmitting])

  const handleRecoverySubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!temporaryToken || !recoveryCode.trim()) return
    setError(null)
    setIsSubmitting(true)
    try {
      await authService.verify2FA(temporaryToken, {
        recovery_code: recoveryCode.trim(),
      })
      queryClient.removeQueries({ queryKey: queryKeys.users.me })
      refreshAuth()
      navigate(from, { replace: true })
    } catch (err) {
      setError(getApiErrorMessage(err, t('login2FA.recoveryCodeError')))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!temporaryToken) {
    return null
  }

  return (
    <AuthCardLayout title={t('login2FA.title')}>
      <form onSubmit={handleRecoverySubmit} className="flex flex-col gap-4">
        {error && (
          <div
            className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4"
            role="alert"
          >
            <p className="text-sm font-normal text-red-600">{error}</p>
          </div>
        )}

        {!useRecoveryCode ? (
          <>
            <p className="text-foreground text-sm">{t('login2FA.totpPrompt')}</p>
            <div>
              <label htmlFor="totp-code" className="mb-2 block text-sm font-semibold text-black">
                {t('login2FA.totpLabel')}
              </label>
              <InputOTP
                id="totp-code"
                length={TOTP_LENGTH}
                value={totpCode}
                onChange={v => setTotpCode(v.replace(/\D/g, ''))}
                inputMode="numeric"
                pattern="[0-9]*"
                disabled={isSubmitting}
                autoFocus
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setUseRecoveryCode(true)
                setError(null)
                setTotpCode('')
              }}
              className="text-primary cursor-pointer text-sm underline hover:opacity-90"
            >
              {t('login2FA.useRecoveryCode')}
            </button>
          </>
        ) : (
          <>
            <p className="text-foreground text-sm">{t('login2FA.recoveryPrompt')}</p>
            <FormInput
              id="recovery-code"
              label={t('login2FA.recoveryCodeLabel')}
              value={recoveryCode}
              onChange={e => setRecoveryCode(e.target.value)}
              placeholder={t('login2FA.recoveryCodePlaceholder')}
              disabled={isSubmitting}
              autoComplete="one-time-code"
            />
            <button
              type="button"
              onClick={() => {
                setUseRecoveryCode(false)
                setError(null)
                setRecoveryCode('')
              }}
              className="text-primary cursor-pointer text-sm underline hover:opacity-90"
            >
              {t('login2FA.useAppCode')}
            </button>
            {recoveryCode.trim() && (
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-primary text-primary-foreground mt-2 w-full cursor-pointer rounded-lg py-2.5 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {isSubmitting ? t('login2FA.submitting') : t('login2FA.submit')}
              </button>
            )}
          </>
        )}
      </form>
    </AuthCardLayout>
  )
}
